import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import { useAuth } from "./auth";
import { useDateRange, qs } from "./dateRange";
import type {
  AlertProfileResponse,
  AnalyzeData,
  ApiKey,
  AssetRow,
  AuditResponse,
  BoardPack,
  Device,
  DetectionZone,
  EventDetail,
  EventFeatureProps,
  EventRow,
  ExportJob,
  FeatureCollection,
  GeoData,
  MapConfig,
  MLModel,
  MonitorSummary,
  Paginated,
  Personnel,
  PromotionRequest,
  RiskMatrixEntry,
  Scorecard,
  SearchResults,
  Site,
  SyncStatus,
  Webhook,
} from "../types/api";

function useTenantKey() {
  const { tenant } = useAuth();
  return tenant?.slug ?? "none";
}

/** Date-range params without `compare` — for endpoints that ignore it, so the
 * Compare toggle doesn't churn their query keys and refire the requests. */
function stripCompare(params: Record<string, string>): Record<string, string> {
  const rest = { ...params };
  delete rest.compare;
  return rest;
}

// ----------------------------------------------------------------- Monitor
export function useMonitorSummary() {
  const t = useTenantKey();
  const { params } = useDateRange();
  return useQuery({
    queryKey: ["monitor-summary", t, params],
    queryFn: async () => (await api.get<MonitorSummary>(`/monitor/summary/${qs(params)}`)).data,
    refetchInterval: 20_000,
  });
}

export function useAlertProfile() {
  const t = useTenantKey();
  const { params } = useDateRange();
  const query = qs(stripCompare(params));
  return useQuery({
    queryKey: ["alert-profile", t, query],
    queryFn: async () =>
      (await api.get<AlertProfileResponse>(`/monitor/alert-profile/${query}`)).data,
    refetchInterval: 30_000,
  });
}

export function useGeo() {
  const t = useTenantKey();
  const { params } = useDateRange();
  const query = qs(stripCompare(params));
  return useQuery({
    queryKey: ["geo", t, query],
    queryFn: async () => (await api.get<GeoData>(`/monitor/geo/${query}`)).data,
    refetchInterval: 60_000,
  });
}

// --------------------------------------------------------------- Mapbox / GeoJSON
export function useMapConfig() {
  return useQuery({
    queryKey: ["map-config"],
    queryFn: async () => (await api.get<MapConfig>("/map/config/")).data,
    // Finite so a token added to the backend is picked up on a normal reload,
    // rather than being cached for the whole session.
    staleTime: 5 * 60_000,
  });
}

export function useMapEvents(
  bbox: string | null,
  zoom: number,
  opts: { severity?: string; end?: string } = {}
) {
  const t = useTenantKey();
  const { params } = useDateRange();
  const merged: Record<string, string | number | boolean | undefined> = {
    ...params, bbox: bbox ?? undefined, zoom: Math.round(zoom), ...opts,
  };
  const query = qs(merged);
  return useQuery({
    queryKey: ["map-events", t, query],
    enabled: !!bbox,
    queryFn: async () =>
      (await api.get<FeatureCollection<EventFeatureProps>>(`/map/events/${query}`)).data,
    placeholderData: (prev) => prev,
    refetchInterval: 60_000,
  });
}

export function useMapSites() {
  const t = useTenantKey();
  const { params } = useDateRange();
  const query = qs(stripCompare(params));
  return useQuery({
    queryKey: ["map-sites", t, query],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queryFn: async () => (await api.get<FeatureCollection<any>>(`/map/sites/${query}`)).data,
    refetchInterval: 60_000,
  });
}

export function useMapAssets() {
  const t = useTenantKey();
  const { params } = useDateRange();
  const query = qs(stripCompare(params));
  return useQuery({
    queryKey: ["map-assets", t, query],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queryFn: async () => (await api.get<FeatureCollection<any>>(`/map/assets/${query}`)).data,
    refetchInterval: 60_000,
  });
}

// ----------------------------------------------------------------- Events
export interface EventFilters {
  severity?: string;
  status?: string;
  site?: number;
  event_type?: number;
  media_type?: string;
  has_media?: boolean;
  min_risk?: number;
  search?: string;
  ordering?: string;
  limit?: number;
}

export function useEvents(filters: EventFilters = {}, useRange = true) {
  const t = useTenantKey();
  const { params } = useDateRange();
  const merged: Record<string, string | number | boolean | undefined> = { ...filters, limit: filters.limit ?? 40 };
  if (useRange) Object.assign(merged, stripCompare(params));
  const query = qs(merged);
  return useQuery({
    queryKey: ["events", t, query],
    queryFn: async () => (await api.get<Paginated<EventRow>>(`/events/${query}`)).data,
    refetchInterval: 15_000,
  });
}

export function useEvent(id: number | null) {
  return useQuery({
    queryKey: ["event", id],
    enabled: id != null,
    queryFn: async () => (await api.get<EventDetail>(`/events/${id}/`)).data,
  });
}

// ------------------------------------------------------------- Cases (full browser)
/** Full faceted filter set for the Cases register. Multi-value facets are arrays. */
export interface CaseFilters {
  severity?: string[];
  status?: string[];
  event_type?: number[];
  site?: number | "";
  asset?: number | "";
  media_type?: string;
  has_media?: boolean;
  dead_man?: boolean;
  min_risk?: number;
  search?: string;
  ordering?: string;
  limit?: number;
  offset?: number;
}

/** Serialize CaseFilters (+ global date range) into an /events/ query string. */
export function buildCaseQuery(f: CaseFilters, rangeParams: Record<string, string> = {}): string {
  const sp = new URLSearchParams();
  const add = (k: string, v: unknown) => {
    if (v !== undefined && v !== null && v !== "") sp.append(k, String(v));
  };
  (f.severity ?? []).forEach((s) => sp.append("severity", s));
  (f.status ?? []).forEach((s) => sp.append("status", s));
  (f.event_type ?? []).forEach((s) => sp.append("event_type", String(s)));
  add("site", f.site);
  add("asset", f.asset);
  add("media_type", f.media_type);
  if (f.has_media) sp.append("has_media", "true");
  if (f.dead_man) sp.append("dead_man", "true");
  add("min_risk", f.min_risk);
  add("search", f.search);
  add("ordering", f.ordering);
  add("limit", f.limit ?? 25);
  add("offset", f.offset);
  for (const [k, v] of Object.entries(rangeParams)) if (k !== "compare") add(k, v);
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export function useCaseList(filters: CaseFilters) {
  const t = useTenantKey();
  const { params } = useDateRange();
  const query = buildCaseQuery(filters, params);
  return useQuery({
    queryKey: ["cases", t, query],
    queryFn: async () => (await api.get<Paginated<EventRow>>(`/events/${query}`)).data,
    placeholderData: (prev) => prev,
  });
}

export interface EventTypeOption {
  id: number;
  code: number;
  label: string;
  slug: string;
  default_severity: string;
  emesrt_level: string;
}

export function useEventTypes() {
  return useQuery({
    queryKey: ["event-types-list"],
    queryFn: async () =>
      (await api.get<Paginated<EventTypeOption>>("/event-types/?limit=100")).data.results,
    staleTime: 5 * 60_000,
  });
}

// ------------------------------------------------------------- Saved views
export interface SavedSearch {
  id: number;
  name: string;
  query: string;
  facets: Record<string, unknown>;
  created_at: string;
}

export function useSavedSearches() {
  const t = useTenantKey();
  return useQuery({
    queryKey: ["saved-searches", t],
    queryFn: async () =>
      (await api.get<Paginated<SavedSearch>>("/saved-searches/?limit=100")).data.results,
  });
}

export function useSaveSearch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name: string; facets: Record<string, unknown>; query?: string }) =>
      (await api.post<SavedSearch>("/saved-searches/", { query: "", ...body })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["saved-searches"] }),
  });
}

export function useDeleteSavedSearch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => (await api.delete(`/saved-searches/${id}/`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["saved-searches"] }),
  });
}

export function useClassify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      (await api.post<EventDetail>(`/events/${id}/classify/`, body)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["event", data.id] });
      qc.invalidateQueries({ queryKey: ["monitor-summary"] });
    },
  });
}

export function useAcknowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) =>
      (await api.post<EventDetail>(`/events/${id}/acknowledge/`, {})).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["event", data.id] });
    },
  });
}

export function useCaseExport() {
  return useMutation({
    mutationFn: async ({ id, fmt }: { id: number; fmt: "pdf" | "json" }) =>
      (await api.post<ExportJob>(`/events/${id}/export/`, { fmt })).data,
  });
}

// ------------------------------------------------------- Integrations sync
export function useSyncStatus() {
  const t = useTenantKey();
  return useQuery({
    queryKey: ["sync-status", t],
    queryFn: async () => (await api.get<SyncStatus>("/integrations/sync/")).data,
    // Poll only while a sync is in flight so the button flips back on its own.
    refetchInterval: (query) => (query.state.data?.running ? 4000 : false),
  });
}

export function useTriggerSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await api.post<{ queued: boolean; running: boolean }>("/integrations/sync/", {})).data,
    onSettled: () => qc.invalidateQueries({ queryKey: ["sync-status"] }),
  });
}

// ----------------------------------------------------------------- Analyze
export function useAnalyze() {
  const t = useTenantKey();
  const { params } = useDateRange();
  return useQuery({
    queryKey: ["analyze", t, params],
    queryFn: async () => (await api.get<AnalyzeData>(`/analyze/${qs(params)}`)).data,
  });
}

export function useRiskMatrix() {
  return useQuery({
    queryKey: ["risk-matrix"],
    queryFn: async () => (await api.get<RiskMatrixEntry[]>("/risk-matrix/")).data,
    staleTime: Infinity,
  });
}

// ----------------------------------------------------------------- Search
export function useSearch(q: string) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ["search", t, q],
    enabled: q.trim().length >= 2,
    queryFn: async () => (await api.get<SearchResults>(`/search/${qs({ q })}`)).data,
  });
}

// ----------------------------------------------------------------- Exports
export function useExports() {
  const t = useTenantKey();
  return useQuery({
    queryKey: ["exports", t],
    queryFn: async () => (await api.get<Paginated<ExportJob>>("/exports/?limit=50")).data.results,
    refetchInterval: 5_000,
  });
}

export function useCreateExport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      kind?: string;
      fmt: string;
      title?: string;
      filters?: Record<string, unknown>;
    }) => (await api.post<ExportJob>("/exports/", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["exports"] }),
  });
}

// ----------------------------------------------------------------- ML
export function useModels() {
  return useQuery({
    queryKey: ["models"],
    queryFn: async () => (await api.get<Paginated<MLModel>>("/models/?limit=50")).data.results,
  });
}

export function usePromotions() {
  return useQuery({
    queryKey: ["promotions"],
    queryFn: async () =>
      (await api.get<Paginated<PromotionRequest>>("/promotions/?limit=50")).data.results,
  });
}

export function usePromoteModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => (await api.post(`/models/${id}/promote/`, {})).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["models"] });
      qc.invalidateQueries({ queryKey: ["promotions"] });
    },
  });
}

// ----------------------------------------------------------------- Comply
export function useScorecards() {
  const t = useTenantKey();
  return useQuery({
    queryKey: ["scorecards", t],
    queryFn: async () =>
      (await api.get<Paginated<Scorecard>>("/scorecards/?limit=60")).data.results,
  });
}

export function useBoardPacks() {
  return useQuery({
    queryKey: ["board-packs"],
    queryFn: async () =>
      (await api.get<Paginated<BoardPack>>("/board-packs/?limit=20")).data.results,
    refetchInterval: 8_000,
  });
}

export function useCreateBoardPack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { title: string; scorecard?: number }) =>
      (await api.post<BoardPack>("/board-packs/", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["board-packs"] }),
  });
}

export function useSignBoardPack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => (await api.post(`/board-packs/${id}/sign/`, {})).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["board-packs"] }),
  });
}

export function useAudit(
  opts: { objectType?: string; objectId?: number | string; limit?: number } = {}
) {
  const t = useTenantKey();
  const query = qs({
    limit: opts.limit ?? 80,
    object_type: opts.objectType,
    object_id: opts.objectId,
  });
  return useQuery({
    queryKey: ["audit", t, query],
    queryFn: async () => (await api.get<AuditResponse>(`/audit/${query}`)).data,
  });
}

// ----------------------------------------------------------------- Configure
export function useSites() {
  const t = useTenantKey();
  return useQuery({
    queryKey: ["sites", t],
    queryFn: async () => (await api.get<Paginated<Site>>("/sites/?limit=100")).data.results,
  });
}

export function useAssets() {
  const t = useTenantKey();
  return useQuery({
    queryKey: ["assets", t],
    queryFn: async () => (await api.get<Paginated<AssetRow>>("/assets/?limit=200")).data.results,
  });
}

export function useDevices() {
  const t = useTenantKey();
  return useQuery({
    queryKey: ["devices", t],
    queryFn: async () => (await api.get<Paginated<Device>>("/devices/?limit=200")).data.results,
  });
}

export function useDetectionZones() {
  const t = useTenantKey();
  return useQuery({
    queryKey: ["detection-zones", t],
    queryFn: async () =>
      (await api.get<Paginated<DetectionZone>>("/detection-zones/?limit=50")).data.results,
  });
}

export function usePersonnel() {
  const t = useTenantKey();
  return useQuery({
    queryKey: ["personnel", t],
    queryFn: async () =>
      (await api.get<Paginated<Personnel>>("/personnel/?limit=200")).data.results,
  });
}

// ----------------------------------------------------------------- Developer
export function useApiKeys() {
  const t = useTenantKey();
  return useQuery({
    queryKey: ["api-keys", t],
    queryFn: async () => (await api.get<Paginated<ApiKey>>("/api-keys/?limit=50")).data.results,
  });
}

export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name: string; scopes?: string[] }) =>
      (await api.post<ApiKey>("/api-keys/", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-keys"] }),
  });
}

export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => (await api.delete(`/api-keys/${id}/`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-keys"] }),
  });
}

export function useWebhooks() {
  const t = useTenantKey();
  return useQuery({
    queryKey: ["webhooks", t],
    queryFn: async () => (await api.get<Paginated<Webhook>>("/webhooks/?limit=50")).data.results,
  });
}
